"use client";

import { useRef } from "react";
import { LegalLinks } from "@/components/legal/legal-links";
import { useModalShell } from "@/hooks/use-modal-shell";
import type { NavItem, NavModel, NavState } from "./app-header-nav";

type AppHeaderNavSheetProps = {
  open: boolean;
  onClose: () => void;
  model: NavModel;
  state: NavState;
  /**
   * Le gestionnaire de la nav en ligne, tel quel. Il porte la sortie du mode
   * `learn`, l'ouverture de la bibliothèque et la navigation par `setView` :
   * en réécrire une variante pour le sheet, c'est se garantir deux
   * comportements pour un seul geste utilisateur.
   */
  onSelect: (item: NavItem) => void;
};

/** L'anneau de focus du sheet. `--color-accent` et non `--color-violet-glow`,
 *  qui à 35 % d'alpha ne se voit pas sur le fond de surface. */
const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent";

const ROW =
  "flex h-11 items-center gap-3 pr-[18px] text-left tracking-tight cursor-pointer transition-colors duration-150 hover:bg-white/[0.04]";

/**
 * La nav principale repliée, sous le palier `compact`.
 *
 * Le panneau est un enfant du header plutôt qu'un portail : il se cale sur
 * `top-full`, donc il suit la hauteur réelle de la barre — y compris quand
 * celle-ci se comprime en paysage — sans jamais rejouer le calcul des zones
 * sûres, déjà appliquées une fois pour toutes par la coque.
 *
 * Le voile s'arrête au bas du header : le bouton menu reste cliquable pour
 * refermer, et le geste « je tape à côté » ne réveille pas le piano en
 * dessous.
 *
 * L'imbrication du Studio y devient un retrait de 12 px, sur des lignes qui
 * gardent leurs 44 px : le panneau fait toujours 276 px, donc le plafond
 * `max-h-[calc(100dvh-5rem)]` n'a pas bougé. Le retrait est doublé d'un `ul`
 * imbriqué, parce que lui seul transmet la hiérarchie à un lecteur d'écran.
 */
export function AppHeaderNavSheet({
  open,
  onClose,
  model,
  state,
  onSelect,
}: AppHeaderNavSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);

  // Piège de focus + Échap, le même que les modales. Un troisième piège maison
  // serait un troisième endroit où corriger le prochain bug clavier.
  useModalShell(sheetRef, open, onClose);

  if (!open) {
    return null;
  }

  const select = (item: NavItem) => {
    onSelect(item);
    onClose();
  };

  // Copie locale : la propriété d'un objet ne garde pas son affinage dans la
  // fermeture du `onClick`, une constante si.
  const exit = model.exit;

  return (
    <>
      <div
        aria-hidden
        onClick={onClose}
        className="[@media(min-width:768px)_and_(not_(max-height:480px))]:hidden absolute inset-x-0 top-full h-[100dvh] bg-glass-backdrop"
      />

      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        tabIndex={-1}
        className="[@media(min-width:768px)_and_(not_(max-height:480px))]:hidden absolute inset-x-0 top-full max-h-[calc(100dvh-5rem)] overflow-y-auto border-b border-border bg-bg-surface py-1.5 outline-none"
        style={{ boxShadow: "0 24px 60px rgba(0,0,0,0.55)" }}
      >
        <nav aria-label="Navigation principale" className="flex flex-col">
          {/* La sortie de l'exercice est une action, pas une destination :
              elle passe en tête, séparée, au lieu de se déguiser en entrée
              « Studio ». Depuis que le Studio a des sous-vues, la déguiser
              revenait à leur donner « Quitter l'exercice » pour parent. */}
          {exit !== null && (
            <>
              <button
                type="button"
                onClick={() => select(exit)}
                className={`${ROW} pl-[18px] text-[14px] ${FOCUS_RING}`}
                style={{
                  color: "var(--color-accent-bright)",
                  fontWeight: 500,
                }}
              >
                <span
                  aria-hidden
                  className="h-4 w-[2px] shrink-0 rounded-full"
                  style={{ background: "var(--color-accent-bright)" }}
                />
                {exit.label}
              </button>
              <div
                aria-hidden
                className="mx-[18px] my-1.5 h-px"
                style={{ background: "var(--color-border-subtle)" }}
              />
            </>
          )}

          <ul className="flex flex-col">
            {model.nodes.map((node) => {
              const current = node.key === state.page;
              // Le Studio reste allumé pendant qu'on travaille dans une de ses
              // vues, sans prendre la barre d'accent de la page courante.
              const parent =
                node.key === "studio" && state.studioActive && !current;

              return (
                <li key={node.key} className="flex flex-col">
                  <button
                    type="button"
                    onClick={() => select(node)}
                    aria-current={current ? "page" : parent ? "true" : undefined}
                    className={`${ROW} pl-[18px] text-[14px] ${FOCUS_RING}`}
                    style={{
                      color: current
                        ? "var(--color-text-primary)"
                        : parent
                          ? "var(--color-text-secondary)"
                          : "var(--color-text-soft)",
                      fontWeight: current ? 600 : parent ? 500 : 400,
                    }}
                  >
                    {/* L'accent passe à gauche : empilée, la nav n'a plus de
                        bas de barre où poser le soulignement de la version en
                        ligne. */}
                    <span
                      aria-hidden
                      className="h-4 w-[2px] shrink-0 rounded-full"
                      style={{
                        background: current
                          ? "var(--color-accent)"
                          : "transparent",
                      }}
                    />
                    {node.label}
                  </button>

                  {node.children.length > 0 && (
                    <ul className="flex flex-col">
                      {node.children.map((child) => {
                        const active = child.key === state.page;

                        return (
                          <li key={child.key} className="flex flex-col">
                            <button
                              type="button"
                              onClick={() => select(child)}
                              aria-current={active ? "page" : undefined}
                              /* 30 px de retrait plus la barre et son écart
                                 posent le libellé à 44 px, soit 12 px après
                                 celui de son parent. */
                              className={`${ROW} pl-[30px] text-[13px] ${FOCUS_RING}`}
                              style={{
                                color: active
                                  ? "var(--color-text-primary)"
                                  : "var(--color-text-soft)",
                                fontWeight: active ? 600 : 400,
                              }}
                            >
                              <span
                                aria-hidden
                                className="h-4 w-[2px] shrink-0 rounded-full"
                                style={{
                                  background: active
                                    ? "var(--color-accent)"
                                    : "transparent",
                                }}
                              />
                              {child.label}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Le pied du panneau, hors du `nav` : ces trois pages ne sont pas des
            destinations de l'application, les ranger dans la navigation
            principale les annoncerait au même rang que la bibliothèque.

            Elles sont ici parce que c'est la seule surface dépliée du palier
            compact, pas parce que c'est leur maison — celle-ci est le pied de
            la popin « Quoi de neuf », qui couvre aussi le palier `expanded` où
            ce sheet n'existe pas (l'iPad en paysage y tombe). Le doublon est
            voulu : il coûte une ligne et évite qu'on cherche.

            Libellés courts : 375 px de large moins les marges, les libellés
            pleins passeraient à la ligne sur un séparateur. */}
        <div
          aria-hidden
          className="mx-[18px] my-1.5 h-px"
          style={{ background: "var(--color-border-subtle)" }}
        />

        <LegalLinks
          labels="short"
          onNavigate={onClose}
          className="px-[18px] pb-1.5 text-[11px] leading-[16px] text-text-muted"
        />
      </div>
    </>
  );
}
